/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the ruler.

   Adaptive horizontal ruler above the paper, ported from the classic
   editor's inline ruler (app.tsx ~3864-4180) with one deliberate change:
   the indent/hanging-indent grab handles live ON THE RULER ITSELF (where
   Word puts them) instead of on the sample body row — the Studio's sample
   body is a plain read-only render of the shared body-layout brain, not a
   hand-rolled LevelRow. Same units, same snapping, same live inch readout,
   same fine-drag-after-1.5s behaviour; only the grab point moved.

   Drives `levels[i].indent` / `levels[i].hangingIndent` through the
   Studio's draft reducer (`edit`), so every drag is a normal, undoable
   forma edit (contract §2.7).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { inToPx, resolvePageGeometry } from '../../shared/body-layout';
import { snapCoarse } from '../utils';
import type { FormaEdit } from '../designer/DesignPalettes';

const RULER_H = 26;
const PX_PER_IN = 96;
const pxToIn = (px: number) => px / PX_PER_IN;

export interface RulerProps {
  forma: StandardConfig;
  /** Which level's indent/hanging markers show — null hides them. */
  levelFocus: number | null;
  edit: FormaEdit;
}

interface DragState {
  type: 'indent' | 'hanging';
  sx: number;
  sv: number;
  fine: boolean;
}

export function Ruler({ forma, levelFocus, edit }: RulerProps) {
  const geo = resolvePageGeometry(forma);
  const pW = inToPx(geo.pageWidthIn);
  const mLpx = inToPx(geo.marginLeftIn);
  const idx = levelFocus != null ? Math.max(0, Math.min(8, levelFocus - 1)) : null;
  const level = idx != null ? forma.levels?.[idx] : null;

  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [liveVal, setLiveVal] = React.useState<number | null>(null);
  const fineTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const snaps = React.useMemo(() => {
    if (idx == null) return [] as number[];
    const out: number[] = [];
    (forma.levels || []).forEach((l, i) => {
      if (i !== idx) {
        out.push(l.indent || 0);
        out.push((l.indent || 0) + (l.hangingIndent || 0));
      }
    });
    return out;
  }, [forma.levels, idx]);

  const findSnap = React.useCallback(
    (v: number): number | null => {
      for (const s of snaps) if (Math.abs(s - v) < 0.08) return s;
      return null;
    },
    [snaps]
  );

  const beginDrag = (type: 'indent' | 'hanging') => (e: React.MouseEvent) => {
    if (!level || idx == null) return;
    e.preventDefault();
    e.stopPropagation();
    const sv = type === 'indent' ? level.indent || 0 : level.hangingIndent || 0;
    if (fineTimer.current) clearTimeout(fineTimer.current);
    fineTimer.current = setTimeout(() => setDrag((d) => (d ? { ...d, fine: true } : d)), 1500);
    setDrag({ type, sx: e.clientX, sv, fine: false });
  };

  React.useEffect(() => {
    if (!drag || idx == null || !level) return;
    const baseIndent = level.indent || 0;
    const onMove = (ev: MouseEvent) => {
      const dxIn = pxToIn(ev.clientX - drag.sx);
      if (drag.type === 'indent') {
        const nv = Math.max(0, drag.sv + dxIn);
        const final = drag.fine ? Math.round(nv * 10000) / 10000 : (findSnap(nv) ?? snapCoarse(nv));
        setLiveVal(final);
        edit((f) => {
          const levels = [...f.levels];
          levels[idx] = { ...levels[idx], indent: final };
          return { ...f, levels };
        });
      } else {
        // Bug fix (Josh, 2026-08-13, Order 3): the hanging marker moves ONLY
        // the paragraph text — the number (indent) never moves with it —
        // clamped at hanging >= 0 so the wrap edge can never sit LEFT of the
        // number. A negative hangingIndent used to be allowed (the wrap
        // edge, indent+hanging, could fall left of indent while the level
        // model's `firstLineTwips` kicked in and pulled the numbered first
        // line back PAST it — shared/body-layout.ts's
        // `w:left`/`w:hanging`/`w:firstLine` law) — that's what read as
        // "number and paragraph move in opposite directions". Clamping at
        // 0 removes the crossing case entirely: hanging only ever grows the
        // gap the indent marker already preserves.
        const minHang = 0;
        let nv = Math.max(minHang, drag.sv + dxIn);
        let final: number;
        if (drag.fine) {
          final = Math.max(minHang, Math.round(nv * 10000) / 10000);
        } else {
          const edge = baseIndent + nv;
          const snapped = findSnap(edge);
          nv = snapped != null ? snapped - baseIndent : snapCoarse(nv);
          final = Math.max(minHang, nv);
        }
        setLiveVal(final);
        edit((f) => {
          const levels = [...f.levels];
          levels[idx] = { ...levels[idx], hangingIndent: final };
          return { ...f, levels };
        });
      }
    };
    const onUp = () => {
      setDrag(null);
      setLiveVal(null);
      if (fineTimer.current) clearTimeout(fineTimer.current);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, idx, level, edit, findSnap]);

  const isDragging = !!drag;
  const indentX = level ? mLpx + inToPx(level.indent || 0) : null;
  const hangX = level ? mLpx + inToPx((level.indent || 0) + (level.hangingIndent || 0)) : null;

  // ─── The ruler's ZERO is the LEFT MARGIN (Josh, 2026-08-13) ───
  // It used to number from the PAGE EDGE while the indent markers were placed
  // at `margin + indent`. So a level at indent 0 sat over the "1" on an
  // inch-margin page and nothing on the ruler agreed with the Indent box or
  // the paper — the opposite of WYSIWYG. Word zeroes its ruler at the margin;
  // so does this. Ticks run negative through the margin, which is shaded.
  const mRpx = inToPx(geo.marginRightIn ?? 0);
  const firstInch = -Math.ceil(geo.marginLeftIn);
  const lastInch = Math.ceil(geo.pageWidthIn - geo.marginLeftIn);
  const inchX = (inch: number) => mLpx + inch * PX_PER_IN;

  return (
    <div className="sfs-ruler" style={{ width: pW, height: RULER_H }} aria-hidden="true">
      {/* The margins, shaded — so "0" is visibly where the text starts. */}
      <div className="sfs-ruler-margin" style={{ left: 0, width: Math.max(0, mLpx) }} />
      <div className="sfs-ruler-margin" style={{ left: pW - mRpx, width: Math.max(0, mRpx) }} />

      {Array.from({ length: lastInch - firstInch + 1 }, (_, k) => {
        const inch = firstInch + k;
        const x = inchX(inch);
        if (x < -1 || x > pW + 1) return null;
        return (
          <React.Fragment key={inch}>
            <span className="sfs-ruler-num" style={{ left: x + 3 }}>
              {Math.abs(inch)}
            </span>
            <div className="sfs-ruler-tick sfs-ruler-tick-major" style={{ left: x }} />
            {inch < lastInch &&
              [1, 2, 3].map((q) => {
                const qx = x + q * (PX_PER_IN / 4);
                if (qx < 0 || qx > pW) return null;
                return (
                  <div
                    key={q}
                    className={`sfs-ruler-tick${q === 2 ? ' sfs-ruler-tick-mid' : ''}`}
                    style={{ left: qx }}
                  />
                );
              })}
          </React.Fragment>
        );
      })}

      {level && indentX != null && hangX != null && (
        <>
          <div
            className="sfs-ruler-span"
            style={{
              left: Math.min(indentX, hangX),
              width: Math.max(1, Math.abs(hangX - indentX)),
            }}
          />
          <div
            role="slider"
            tabIndex={0}
            aria-label={`Level ${levelFocus} indent — ${(level.indent || 0).toFixed(2)} inches`}
            aria-valuenow={level.indent || 0}
            title={`Indent · ${(level.indent || 0).toFixed(2)}″ — drag to change`}
            className={`sfs-ruler-handle sfs-ruler-handle-indent${drag?.type === 'indent' ? ' is-active' : ''}`}
            style={{ left: indentX }}
            onMouseDown={beginDrag('indent')}
            onKeyDown={(e) => {
              if (idx == null) return;
              const step = e.shiftKey ? 0.0625 : 0.25;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                edit((f) => {
                  const levels = [...f.levels];
                  const cur = levels[idx].indent || 0;
                  levels[idx] = { ...levels[idx], indent: Math.max(0, cur + dir * step) };
                  return { ...f, levels };
                });
              }
            }}
          />
          <div
            role="slider"
            tabIndex={0}
            aria-label={`Level ${levelFocus} hanging indent — ${(level.hangingIndent || 0).toFixed(2)} inches`}
            aria-valuenow={level.hangingIndent || 0}
            title={`Hanging · ${((level.indent || 0) + (level.hangingIndent || 0)).toFixed(2)}″ — drag to change`}
            className={`sfs-ruler-handle sfs-ruler-handle-hanging${drag?.type === 'hanging' ? ' is-active' : ''}`}
            style={{ left: hangX }}
            onMouseDown={beginDrag('hanging')}
            onKeyDown={(e) => {
              if (idx == null) return;
              const step = e.shiftKey ? 0.0625 : 0.25;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                edit((f) => {
                  const levels = [...f.levels];
                  const cur = levels[idx].hangingIndent || 0;
                  // Same clamp as the drag (Order 3) — hanging never goes
                  // negative, so keyboard nudges can't reintroduce the
                  // crossing bug either.
                  levels[idx] = {
                    ...levels[idx],
                    hangingIndent: Math.max(0, cur + dir * step),
                  };
                  return { ...f, levels };
                });
              }
            }}
          />
        </>
      )}

      {isDragging && liveVal != null && (
        <div
          className="sfs-ruler-readout"
          style={{ left: (drag!.type === 'indent' ? indentX : hangX) ?? 0 }}
        >
          {liveVal.toFixed(2)}″ {drag!.type === 'indent' ? 'indent' : 'hanging'}
        </div>
      )}
    </div>
  );
}
